<template>
  <div class="animated fadeIn">
    <!-- Tabela usando componente Tables padrão -->
    <Tables
      ref="tableGrid"
      :columns="[
        'Data/Hora',
        'Tipo',
        'Iniciada Por',
        'Duração',
        'Total',
        '✅ Criados',
        '🔄 Atualizados',
        '❌ Falhas',
        'Status',
        'Ações'
      ]"
      :showReload="true"
      :showSearch="true"
      :showPaging="true"
      :showTipFooter="false"
      v-on:Load="onLoad"
      v-on:Reload="onReload"
      v-on:Search="onShowSearch"
    >
      <tr v-for="exec in filteredExecutions" :key="exec.id" :class="getRowClass(exec)">
        <td class="text-center">
          <strong>{{ formatDateTime(exec.startTime) }}</strong>
          <br>
          <small class="text-muted">
            <i class="fa fa-clock-o"></i>
            {{ formatRelativeTime(exec.startTime) }}
          </small>
          <br>
          <b-badge v-if="isRecent(exec.startTime)" variant="success" class="mt-1">
            <i class="fa fa-star"></i> Nova
          </b-badge>
        </td>
        <td class="text-center">
          <b-badge :variant="getModeVariant(exec.mode)">
            {{ getModeLabel(exec.mode) }}
          </b-badge>
        </td>
        <td class="text-center">
          <strong>{{ exec.triggeredBy || '-' }}</strong>
        </td>
        <td class="text-center">
          <span>{{ formatDuration(exec.durationSeconds) }}</span>
        </td>
        <td class="text-center">
          <strong>{{ exec.totalUsers || 0 }}</strong>
        </td>
        <td class="text-center">
          <b-badge variant="success" pill>{{ exec.createdUsers || 0 }}</b-badge>
        </td>
        <td class="text-center">
          <b-badge variant="info" pill>{{ exec.updatedUsers || 0 }}</b-badge>
        </td>
        <td class="text-center">
          <b-badge variant="danger" pill>{{ exec.failedUsers || 0 }}</b-badge>
          <br v-if="exec.failedUsers > 0 && exec.errorMessage">
          <small v-if="exec.failedUsers > 0 && exec.errorMessage" class="text-danger">
            <i class="fa fa-exclamation-triangle"></i>
            {{ truncateError(exec.errorMessage) }}
          </small>
        </td>
        <td class="text-center">
          <b-badge :variant="getStatusVariant(exec.status)">
            {{ getStatusLabel(exec.status) }}
          </b-badge>
        </td>
        <td class="text-center">
          <button
            class="btn btn-sm btn-primary"
            @click="$emit('view-details', exec.executionId)"
            title="Ver Detalhes"
            v-b-tooltip.hover
          >
            <i class="fa fa-eye"></i>
          </button>
        </td>
      </tr>
    </Tables>

    <!-- Sidebar de Filtros -->
    <SidebarFilters
      :filters="filters"
      @search="onSearch"
    />
  </div>
</template>

<script>
import moment from 'moment'
import Tables from '@/components/forms/Tables'
import SidebarFilters from './SidebarFilters.vue'

export default {
  name: 'ExecutionsTable',
  components: {
    Tables,
    SidebarFilters
  },
  props: {
    executions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      filters: {
        status: '',
        mode: '',
        triggeredBy: ''
      }
    }
  },
  computed: {
    hasActiveFilters () {
      return this.filters.status || this.filters.mode || this.filters.triggeredBy
    },
    filteredExecutions () {
      if (!this.executions) return []

      let filtered = [...this.executions]

      if (this.filters.status) {
        filtered = filtered.filter(exec => exec.status === this.filters.status)
      }

      if (this.filters.mode) {
        filtered = filtered.filter(exec => exec.mode === this.filters.mode)
      }

      if (this.filters.triggeredBy) {
        const query = this.filters.triggeredBy.toLowerCase()
        filtered = filtered.filter(exec =>
          exec.triggeredBy && exec.triggeredBy.toLowerCase().includes(query)
        )
      }

      return filtered
    }
  },
  methods: {
    onLoad () {
      this.$emit('refresh')
      this.loadTableData()
    },
    onReload () {
      this.$emit('refresh')
    },
    onShowSearch () {
      this.$root.$emit('bv::toggle::collapse', 'sidebar-1')
    },
    onSearch (filters) {
      this.filters = filters
      this.$refs.tableGrid.Clear()
      this.$nextTick(() => {
        this.loadTableData()
      })
    },
    loadTableData () {
      if (this.$refs.tableGrid) {
        this.$refs.tableGrid.loadAllByDataWithNoResponse(this.filteredExecutions)
      }
    },
    applyFilters () {
      this.loadTableData()
    },
    clearFilters () {
      this.filters = {
        status: '',
        mode: '',
        triggeredBy: ''
      }
      this.loadTableData()
    },
    formatDateTime (timestamp) {
      return moment(timestamp).format('DD/MM/YYYY HH:mm:ss')
    },
    formatRelativeTime (timestamp) {
      return moment(timestamp).fromNow()
    },
    formatDuration (seconds) {
      if (!seconds) return '-'
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}m ${secs}s`
    },
    isRecent (timestamp) {
      const now = moment()
      const time = moment(timestamp)
      return now.diff(time, 'minutes') < 5
    },
    getModeLabel (mode) {
      const labels = {
        'FULL': 'Completa',
        'MANUAL': 'Manual',
        'INCREMENTAL': 'Incremental'
      }
      return labels[mode] || mode
    },
    getModeVariant (mode) {
      const variants = {
        'FULL': 'primary',
        'MANUAL': 'warning',
        'INCREMENTAL': 'info'
      }
      return variants[mode] || 'secondary'
    },
    getStatusLabel (status) {
      const labels = {
        'COMPLETED': 'Sucesso',
        'PARTIAL': 'Parcial',
        'FAILED': 'Falhou'
      }
      return labels[status] || status
    },
    getStatusVariant (status) {
      const variants = {
        'COMPLETED': 'success',
        'PARTIAL': 'warning',
        'FAILED': 'danger'
      }
      return variants[status] || 'secondary'
    },
    getRowClass (exec) {
      if (exec.status === 'FAILED') return 'table-danger'
      if (exec.status === 'PARTIAL') return 'table-warning'
      return ''
    },
    truncateError (error) {
      if (!error) return ''
      return error.length > 50 ? error.substring(0, 50) + '...' : error
    }
  },
  watch: {
    executions: {
      handler () {
        this.loadTableData()
      },
      deep: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.loadTableData()
    })
  }
}
</script>

<style scoped>
.table-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05) !important;
}
</style>



// WEBPACK FOOTER //
// src/views/ADSync/components/dashboard/ExecutionsTable.vue